import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bug, ChevronUp, Target, Database, Hash } from 'lucide-react';
import { getMessageActionClassName, MESSAGE_ACTION_STYLES } from '../utils/messageActionStyles';

// Constants
const ICON_SIZES = {
  SMALL: 12,
  MEDIUM: 14,
  LARGE: 16
};

const ANIMATION_DURATIONS = {
  FAST: 0.15,
  NORMAL: 0.3
};

const DIMENSIONS = {
  TOOLTIP_MIN_WIDTH: 280,
  PANEL_MIN_WIDTH: 350,
  PANEL_MAX_WIDTH: 500,
  DIALOG_ID_PREVIEW_LENGTH: 8
};

const COLORS = {
  PURPLE: {
    LIGHT: 'text-purple-500',
    MEDIUM: 'text-purple-600 dark:text-purple-400',
    DARK: 'text-purple-700 dark:text-purple-300',
    BACKGROUND: 'bg-purple-100 dark:bg-purple-900/30',
    BACKGROUND_LIGHT: 'bg-purple-50 dark:bg-purple-900/20'
  },
  BLUE: {
    LIGHT: 'text-blue-500',
    MEDIUM: 'text-blue-600 dark:text-blue-400',
    DARK: 'text-blue-700 dark:text-blue-300',
    BACKGROUND: 'bg-blue-50 dark:bg-blue-900/20',
    BACKGROUND_LIGHT: 'bg-blue-100 dark:bg-blue-800'
  },
  GRAY: {
    LIGHT: 'text-gray-400 dark:text-gray-500',
    MEDIUM: 'text-gray-500 dark:text-gray-400',
    DARK: 'text-gray-600 dark:text-gray-400',
    BACKGROUND: 'bg-gray-50 dark:bg-gray-700',
    HOVER: 'hover:bg-gray-100 dark:hover:bg-gray-700'
  }
};

const TEXT_SIZES = {
  XS: 'text-xs',
  SM: 'text-sm'
};

const SPACING = {
  GAP_SMALL: 'gap-2',
  GAP_MEDIUM: 'gap-3',
  MARGIN_SMALL: 'mb-1',
  MARGIN_MEDIUM: 'mb-2',
  MARGIN_LARGE: 'mb-3',
  PADDING_SMALL: 'p-1',
  PADDING_MEDIUM: 'p-2',
  PADDING_LARGE: 'p-3',
  PADDING_XL: 'p-4'
};

const DEFAULT_VALUES = {
  UNKNOWN_INTENT: 'Unknown Intent',
  NOT_AVAILABLE: 'N/A',
  DEBUG_TITLE: 'Debug Information',
  CLOSE_LABEL: 'Close debug information',
  CLICK_HINT: 'Click for details'
};

const DebugInfoButton = ({ debugInfo, messageId }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Memoized formatting functions for performance
  const formatIntent = useCallback((intent) => {
    if (!intent) return DEFAULT_VALUES.UNKNOWN_INTENT;
    return intent
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/Intent$/, '');
  }, []);

  const formatEntities = useCallback((entities) => {
    if (!entities || Object.keys(entities).length === 0) return [];
    return Object.entries(entities).map(([key, value]) => ({
      key: key.replace(/([A-Z])/g, ' $1').trim(),
      value: value || DEFAULT_VALUES.NOT_AVAILABLE
    }));
  }, []);

  // Memoized computed values
  const entities = useMemo(() => {
    if (!debugInfo?.extracted_entities) return [];
    return formatEntities(debugInfo.extracted_entities);
  }, [debugInfo?.extracted_entities, formatEntities]);

  const formattedIntent = useMemo(() => {
    if (!debugInfo?.intent) return DEFAULT_VALUES.UNKNOWN_INTENT;
    return formatIntent(debugInfo.intent);
  }, [debugInfo?.intent, formatIntent]);

  // Memoized quick info for tooltip
  const quickInfo = useMemo(() => ({
    intent: formattedIntent,
    entityCount: entities.length,
    dialogId: debugInfo?.dialog_id ? debugInfo.dialog_id.substring(0, DIMENSIONS.DIALOG_ID_PREVIEW_LENGTH) + '...' : DEFAULT_VALUES.NOT_AVAILABLE
  }), [formattedIntent, entities.length, debugInfo?.dialog_id]);

  // Event handlers
  const handleToggleExpanded = useCallback(() => setIsExpanded(prev => !prev), []);
  const handleCloseExpanded = useCallback(() => setIsExpanded(false), []);

  // Don't render if no debug info - after all hooks
  if (!debugInfo) return null;

  const { dialog_id } = debugInfo;

  return (
    <div className="relative">
      {/* Debug Button */}
      <motion.button
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleToggleExpanded}
        className={getMessageActionClassName('debug', isHovered || isExpanded, false)}
        title={DEFAULT_VALUES.DEBUG_TITLE}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Bug size={MESSAGE_ACTION_STYLES.ICON_SIZE} />
      </motion.button>

      {/* Hover Tooltip */}
      <AnimatePresence>
        {isHovered && !isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 5 }}
            transition={{ duration: ANIMATION_DURATIONS.FAST }}
            className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50"
          >
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 ${SPACING.PADDING_LARGE} min-w-[${DIMENSIONS.TOOLTIP_MIN_WIDTH}px]`}>
              <div className={`flex items-center ${SPACING.GAP_SMALL} ${SPACING.MARGIN_MEDIUM}`}>
                <Target size={ICON_SIZES.SMALL} className={COLORS.PURPLE.LIGHT} />
                <span className={`${TEXT_SIZES.XS} font-medium text-gray-700 dark:text-gray-300`}>
                  {quickInfo.intent}
                </span>
              </div>
              <div className={`flex items-center ${SPACING.GAP_SMALL} ${SPACING.MARGIN_SMALL}`}>
                <Database size={ICON_SIZES.SMALL} className={COLORS.BLUE.LIGHT} />
                <span className={`${TEXT_SIZES.XS} ${COLORS.GRAY.DARK}`}>
                  {quickInfo.entityCount} entities
                </span>
              </div>
              <div className={`flex items-center ${SPACING.GAP_SMALL}`}>
                <Hash size={ICON_SIZES.SMALL} className={COLORS.GRAY.MEDIUM} />
                <span className={`${TEXT_SIZES.XS} ${COLORS.GRAY.MEDIUM} font-mono`}>
                  {quickInfo.dialogId}
                </span>
              </div>
              <div className={`${TEXT_SIZES.XS} ${COLORS.GRAY.LIGHT} mt-2 text-center`}>
                {DEFAULT_VALUES.CLICK_HINT}
              </div>
            </div>
            {/* Tooltip Arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200 dark:border-t-gray-700"></div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            transition={{ duration: ANIMATION_DURATIONS.NORMAL, ease: "easeInOut" }}
            className="absolute bottom-full left-0 mb-2 z-50"
          >
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 ${SPACING.PADDING_XL} min-w-[${DIMENSIONS.PANEL_MIN_WIDTH}px] max-w-[${DIMENSIONS.PANEL_MAX_WIDTH}px]`}>
              {/* Header */}
              <div className={`flex items-center justify-between ${SPACING.MARGIN_LARGE}`}>
                <div className={`flex items-center ${SPACING.GAP_SMALL}`}>
                  <Bug size={ICON_SIZES.LARGE} className={COLORS.PURPLE.LIGHT} />
                  <span className={`${TEXT_SIZES.SM} font-semibold text-gray-700 dark:text-gray-300`}>
                    {DEFAULT_VALUES.DEBUG_TITLE}
                  </span>
                </div>
                <button
                  onClick={handleCloseExpanded}
                  className={`${SPACING.PADDING_SMALL} rounded-full ${COLORS.GRAY.HOVER} transition-colors`}
                  aria-label={DEFAULT_VALUES.CLOSE_LABEL}
                >
                  <ChevronUp size={ICON_SIZES.MEDIUM} className={COLORS.GRAY.MEDIUM} />
                </button>
              </div>

              {/* Intent */}
              <div className={SPACING.MARGIN_LARGE}>
                <div className={`flex items-center ${SPACING.GAP_SMALL} ${SPACING.MARGIN_SMALL}`}>
                  <Target size={ICON_SIZES.MEDIUM} className={COLORS.PURPLE.LIGHT} />
                  <span className={`${TEXT_SIZES.XS} font-medium ${COLORS.GRAY.DARK}`}>
                    Intent
                  </span>
                </div>
                <div className={`${COLORS.PURPLE.BACKGROUND_LIGHT} rounded-md ${SPACING.PADDING_MEDIUM}`}>
                  <span className={`${TEXT_SIZES.SM} ${COLORS.PURPLE.DARK} font-medium`}>
                    {formattedIntent}
                  </span>
                </div>
              </div>

              {/* Entities */}
              {entities.length > 0 && (
                <div className={SPACING.MARGIN_LARGE}>
                  <div className={`flex items-center ${SPACING.GAP_SMALL} ${SPACING.MARGIN_MEDIUM}`}>
                    <Database size={ICON_SIZES.MEDIUM} className={COLORS.BLUE.LIGHT} />
                    <span className={`${TEXT_SIZES.XS} font-medium ${COLORS.GRAY.DARK}`}>
                      Entities ({entities.length})
                    </span>
                  </div>
                  <div className="space-y-1">
                    {entities.map((entity, index) => (
                      <div key={index} className={`${COLORS.BLUE.BACKGROUND} rounded-md ${SPACING.PADDING_MEDIUM}`}>
                        <div className="flex justify-between items-center">
                          <span className={`${TEXT_SIZES.XS} ${COLORS.BLUE.MEDIUM} font-medium`}>
                            {entity.key}
                          </span>
                          <span className={`${TEXT_SIZES.XS} ${COLORS.BLUE.DARK} font-mono ${COLORS.BLUE.BACKGROUND_LIGHT} px-2 py-0.5 rounded`}>
                            {entity.value}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dialog ID */}
              <div className={SPACING.MARGIN_MEDIUM}>
                <div className={`flex items-center ${SPACING.GAP_SMALL} ${SPACING.MARGIN_SMALL}`}>
                  <Hash size={ICON_SIZES.MEDIUM} className={COLORS.GRAY.MEDIUM} />
                  <span className={`${TEXT_SIZES.XS} font-medium ${COLORS.GRAY.DARK}`}>
                    Dialog ID
                  </span>
                </div>
                <div className={`${COLORS.GRAY.BACKGROUND} rounded-md ${SPACING.PADDING_MEDIUM}`}>
                  <span className={`${TEXT_SIZES.XS} ${COLORS.GRAY.DARK} font-mono break-all`}>
                    {dialog_id || DEFAULT_VALUES.NOT_AVAILABLE}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className={`${TEXT_SIZES.XS} ${COLORS.GRAY.LIGHT} text-center pt-2 border-t border-gray-200 dark:border-gray-600`}>
                Message ID: {messageId}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DebugInfoButton;

